import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, BookOpen, HeartHandshake } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';
import NationPicker from '../components/NationPicker';

/**
 * "Was it abuse or assault?" — a survivor-facing page for anyone
 * doubting whether what happened to them counts as assault, abuse or
 * coercive control. Warm, plain-language, non-judgemental. Links out
 * to Consent, DA, SV process and Support.
 *
 * Deliberately shorter than the Consent guide. Grouped so a reader can
 * jump to the situation that feels closest to theirs, then read the
 * "But…" section that quietly rebuts the thoughts that keep survivors
 * doubting themselves.
 */

const LINE_GROUPS = [
  {
    key: 'physical',
    title: 'Physical',
    items: [
      'Any unwanted touch, grabbing, hitting, pushing or restraining.',
      'Blocking your exit from a room, car, or house.',
      'Throwing things at you or breaking your belongings.',
      'Hurting a pet as a way of hurting you.',
      'Any of the above when they were "just drunk", "in a mood", or "didn\u2019t really mean it".',
    ],
  },
  {
    key: 'sexual',
    title: 'Sexual',
    items: [
      'Kissing, touching or undressing you without a clear yes.',
      'Carrying on after you said no, went quiet, or froze.',
      'Removing a condom, or agreeing to use one and then not doing so.',
      'Sending, sharing or demanding intimate images \u2014 or threatening to.',
      'Pressuring, badgering or wearing you down until you agreed.',
      'Anything sexual with you while you were asleep, unconscious, or too drunk / drugged to choose.',
    ],
  },
  {
    key: 'emotional',
    title: 'Emotional &amp; verbal',
    items: [
      'Name-calling, ridicule, humiliation \u2014 in private or in front of others.',
      'Silent treatment or withdrawing affection to punish you.',
      'Telling you what happened didn\u2019t happen, or that you\u2019re "too sensitive" ("gaslighting").',
      'Threats \u2014 to hurt you, hurt themselves, take the children, out you, or ruin your reputation.',
    ],
  },
  {
    key: 'coercive',
    title: 'Coercive control',
    items: [
      'Deciding what you wear, who you see, when you leave the house.',
      'Checking your phone, messages or receipts \u2014 with or without your knowledge.',
      'Isolating you from friends, family or work.',
      'Making you account for every hour of your day.',
      'Punishing you \u2014 with anger, silence or withdrawal \u2014 when you push back.',
    ],
  },
  {
    key: 'financial',
    title: 'Financial',
    items: [
      'Controlling how much money you can spend, or on what.',
      'Running up debt in your name, or refusing to let you have your own account.',
      'Sabotaging your job \u2014 turning up, ringing your workplace, making you late.',
      'Stopping you from working or studying.',
    ],
  },
  {
    key: 'digital',
    title: 'Digital',
    items: [
      'Tracking your location without your agreement.',
      'Reading your messages, emails or search history.',
      'Demanding your passwords \u2014 or having them "just in case".',
      'Monitoring your social media, or making you post, delete or unfollow.',
      'Ringing or texting many times a day to "check on you".',
    ],
  },
  {
    key: 'trust',
    title: 'Someone in a position of trust',
    items: [
      'A teacher, tutor, coach, doctor, therapist, faith leader, youth worker, carer or boss.',
      'Anything sexual, romantic or overly personal \u2014 even if you agreed at the time.',
      'Gifts, favours or secrets that made you feel special or singled out.',
      'Meetings that "had to be private", messaging outside working hours, closed doors.',
    ],
  },
];

const BUT_ITEMS = [
  {
    excuse: '\u201cBut they said sorry.\u201d',
    rebuttal:
      'Sorry doesn\u2019t undo it. Hurt-and-apologise cycles are the classic pattern of abuse.',
  },
  {
    excuse: '\u201cBut I was drunk.\u201d',
    rebuttal:
      'Too drunk to choose means you didn\u2019t consent. Alcohol removes capacity, not responsibility from the other person.',
  },
  {
    excuse: '\u201cBut we\u2019re in a relationship.\u201d',
    rebuttal:
      'Being together isn\u2019t standing consent. Every act still needs agreement, freedom and capacity \u2014 every time.',
  },
  {
    excuse: '\u201cBut they never hit me.\u201d',
    rebuttal:
      'Hitting isn\u2019t the threshold. Coercive control, emotional abuse and financial abuse all cross the line.',
  },
  {
    excuse: '\u201cBut I laughed / went along with it.\u201d',
    rebuttal:
      'Freeze and fawn are trauma responses \u2014 not consent. Complying to survive is still surviving.',
  },
  {
    excuse: '\u201cBut nothing physical happened.\u201d',
    rebuttal:
      'Emotional harm is real harm. You are allowed to name it, and to leave because of it.',
  },
  {
    excuse: '\u201cBut it was only once.\u201d',
    rebuttal:
      'Once is enough. Naming what happened isn\u2019t a scale you have to fill.',
  },
  {
    excuse: '\u201cBut they said they love me.\u201d',
    rebuttal:
      'Love and harm are not opposites. Someone can care about you and still cross a line.',
  },
  {
    excuse: '\u201cBut nobody will believe me.\u201d',
    rebuttal:
      'You do not need anyone else to believe you before you get support. Specialist services believe survivors as a matter of practice.',
  },
];

const SOURCES = [
  { label: "Women's Aid \u2014 What is domestic abuse?", url: 'https://www.womensaid.org.uk/information-support/what-is-domestic-abuse/' },
  { label: 'Refuge \u2014 Types of domestic abuse', url: 'https://refuge.org.uk/i-need-help-now/what-is-domestic-abuse/' },
  { label: 'Rape Crisis E&W \u2014 About sexual violence', url: 'https://rapecrisis.org.uk/get-informed/about-sexual-violence/' },
  { label: 'SafeLives \u2014 Recognising domestic abuse', url: 'https://safelives.org.uk/knowledge-hub/spotlights/' },
  { label: 'gov.uk \u2014 Controlling or coercive behaviour', url: 'https://www.gov.uk/government/publications/statutory-guidance-framework-controlling-or-coercive-behaviour-in-an-intimate-or-family-relationship' },
  { label: 'NSPCC \u2014 What is grooming?', url: 'https://www.nspcc.org.uk/what-is-child-abuse/types-of-abuse/grooming/' },
];

function GroupCard({ group }) {
  return (
    <div className="tf-card p-5" data-testid={`crossed-group-${group.key}`}>
      <h3
        className="font-heading"
        style={{
          fontSize: 18,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
          margin: 0,
        }}
        dangerouslySetInnerHTML={{ __html: group.title }}
      />
      <ul
        style={{ listStyle: 'none', padding: 0, margin: '10px 0 0 0' }}
        className="flex flex-col gap-2"
      >
        {group.items.map((t) => (
          <li key={t} className="flex items-start gap-2.5">
            <span
              aria-hidden="true"
              style={{
                marginTop: 8,
                flexShrink: 0,
                width: 6,
                height: 6,
                borderRadius: 999,
                background: 'var(--tf-purple-dark)',
              }}
            />
            <span
              className="font-body"
              style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--tf-text)' }}
            >
              {t}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Crossed() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="crossed-screen">
      <SEO
        title="Was it abuse or assault?"
        description="If you're unsure whether what happened counts as abuse, assault or coercive control, this trauma-informed UK guide can help you name it — with quiet answers to the doubts that stop survivors naming it."
        path="/app/crossed-the-line"
      />

      <p className="eyebrow">FOR ANYONE DOUBTING WHAT HAPPENED</p>
      <h1
        className="font-heading mt-3"
        data-testid="crossed-title"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        Was it abuse or assault?
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
      >
        If you&apos;re even asking this question, your instincts are already telling you
        something. Your feelings are information. You don&apos;t need proof, and you
        don&apos;t need to name it before you get support. The signs are the same UK-wide —
        the law that names them differs, so choose your nation below.
      </p>

      <NationPicker testid="crossed-nation-picker" />

      <HelplinesStrip variant="sv" />

      {/* Trust your gut reassurance */}
      <section
        className="mt-6 tf-card p-5 sm:p-6"
        data-testid="crossed-reassurance"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
          >
            <HeartHandshake size={20} />
          </div>
          <div>
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              BEFORE YOU READ ON
            </p>
            <h2
              className="font-heading mt-1"
              style={{
                fontSize: 'clamp(19px, 2.4vw, 22px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              Trust your gut.
            </h2>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
            >
              You don&apos;t need a checklist to end a relationship, a friendship, or a
              job. You don&apos;t need "proof" to feel unsafe. The list below is here to
              help you name what happened — not to test you.
            </p>
          </div>
        </div>
      </section>

      <div className="mt-6">
        <PrintButton label="Print / Save this page" />
      </div>

      {/* Section 01 — what crossing a line looks like */}
      <section className="mt-12" data-testid="crossed-section-groups">
        <p className="eyebrow">SECTION 01</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          What abuse or assault can look like.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Not every situation looks the same. Skim the groups below and stop at any that
          feel close to yours. Even one item can be enough.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {LINE_GROUPS.map((g) => (
            <GroupCard key={g.key} group={g} />
          ))}
        </div>
      </section>

      {/* Section 02 — the "but…" section */}
      <section className="mt-12" data-testid="crossed-section-but">
        <p className="eyebrow">SECTION 02</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          The thoughts that stop people naming it.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Almost every survivor has thought at least one of these. They are quiet, and
          they are strong. Here is a gentler answer to each.
        </p>
        <ul className="mt-4 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
          {BUT_ITEMS.map((b) => (
            <li key={b.excuse} className="tf-card p-4" data-testid="crossed-but-item">
              <p
                className="font-body"
                style={{
                  fontSize: 14,
                  color: 'var(--tf-text)',
                  fontStyle: 'italic',
                  fontWeight: 700,
                  lineHeight: 1.55,
                }}
              >
                {b.excuse}
              </p>
              <p
                className="font-body mt-1.5"
                style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
              >
                {b.rebuttal}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* Closing reassurance */}
      <section
        className="mt-12 rounded-2xl p-5"
        data-testid="crossed-closing"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
      >
        <p
          className="font-body text-center"
          style={{ fontSize: 13.5, color: 'var(--tf-text)', lineHeight: 1.7 }}
        >
          If any of this matches, it wasn&apos;t your fault, and you are allowed to name
          it.<br />
          <strong>You are believed. Take the next step at your own pace.</strong>
        </p>
      </section>

      {/* Next steps */}
      <section className="mt-10" data-testid="crossed-next">
        <p className="eyebrow">WHERE TO NEXT</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Link
            to="/app/consent"
            data-testid="crossed-cta-consent"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Consent — what it means in UK law
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
          <Link
            to="/app/domestic-abuse"
            data-testid="crossed-cta-da"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Domestic abuse — the calm guide
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
          <Link
            to="/app/process"
            data-testid="crossed-cta-process"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              If you&apos;re thinking about reporting (SV)
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
          <Link
            to="/app/support"
            data-testid="crossed-cta-support"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Specialist UK helplines
            </span>
            <ArrowRight size={15} color="var(--tf-purple-dark)" />
          </Link>
        </div>
      </section>

      {/* Sources */}
      <section className="mt-12" data-testid="crossed-sources">
        <div className="flex items-center gap-2">
          <BookOpen size={15} color="var(--tf-purple-dark)" aria-hidden="true" />
          <p className="eyebrow">SOURCES &amp; FURTHER READING</p>
        </div>
        <ul className="mt-4 flex flex-col gap-2" style={{ listStyle: 'none', padding: 0 }}>
          {SOURCES.map((s) => (
            <li key={s.url}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="tf-link"
                style={{ fontSize: 13, lineHeight: 1.55 }}
                dangerouslySetInnerHTML={{ __html: `${s.label} \u2192` }}
              />
            </li>
          ))}
        </ul>
        <p
          className="font-body mt-5"
          style={{
            fontSize: 11.5,
            color: 'var(--tf-text-muted)',
            lineHeight: 1.55,
          }}
        >
          This page is a plain-language guide, not legal advice. For advice about your
          own situation, please speak to a specialist ISVA, IDVA, Rape Crisis centre or
          solicitor.
        </p>
      </section>
    </div>
  );
}
